import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, TrendingUp, Clock, AlertTriangle, RefreshCw, LogOut,
  Eye, UserPlus, CreditCard, Mail, Loader2, ChevronRight,
  BarChart3, Calendar, Shield, Globe, PieChart, Download, FileSpreadsheet,
  Phone, MapPin, User, Trophy, Award, Target, Scale, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import * as XLSX from 'xlsx';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Exit survey reasons (must match backend)
const EXIT_REASONS = [
  { id: 'too_expensive', label: 'Too expensive' },
  { id: 'not_ready', label: 'Not ready to commit' },
  { id: 'found_alternative', label: 'Found another solution' },
  { id: 'just_browsing', label: 'Just browsing' },
  { id: 'other', label: 'Other reason' },
];

// Helper function to get country flag emoji
const getCountryFlag = (country) => {
  const countryFlags = {
    'Australia': '🇦🇺',
    'United States': '🇺🇸',
    'USA': '🇺🇸',
    'United Kingdom': '🇬🇧',
    'UK': '🇬🇧',
    'Canada': '🇨🇦',
    'Germany': '🇩🇪',
    'France': '🇫🇷',
    'Spain': '🇪🇸',
    'Italy': '🇮🇹',
    'Japan': '🇯🇵',
    'China': '🇨🇳',
    'India': '🇮🇳',
    'Brazil': '🇧🇷',
    'Mexico': '🇲🇽',
    'South Korea': '🇰🇷',
    'Netherlands': '🇳🇱',
    'Sweden': '🇸🇪',
    'Norway': '🇳🇴',
    'Denmark': '🇩🇰',
    'Finland': '🇫🇮',
    'New Zealand': '🇳🇿',
    'Singapore': '🇸🇬',
    'Ireland': '🇮🇪',
    'Switzerland': '🇨🇭',
    'Austria': '🇦🇹',
    'Belgium': '🇧🇪',
    'Portugal': '🇵🇹',
    'Poland': '🇵🇱',
    'Russia': '🇷🇺',
    'South Africa': '🇿🇦',
    'Unknown': '🌍'
  };
  return countryFlags[country] || '🌍';
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [internalError, setInternalError] = useState('');
  const [sendingReport, setSendingReport] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [activeTab, setActiveTab] = useState('overview');
  const [subscribers, setSubscribers] = useState([]);
  const [loadingSubscribers, setLoadingSubscribers] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [challengeParticipants, setChallengeParticipants] = useState([]);
  const [loadingChallenge, setLoadingChallenge] = useState(false);
  const [exitSurveys, setExitSurveys] = useState({ surveys: [], reason_stats: [], total: 0 });
  const [loadingExitSurveys, setLoadingExitSurveys] = useState(false);

  // Computed error that filters out browser noise
  const error = (() => {
    if (!internalError) return '';
    const msg = internalError.toLowerCase();
    if (msg.includes('postmessage') || 
        msg.includes('could not be cloned') ||
        msg.includes('request object') ||
        msg.includes('cloned') ||
        msg.includes('script error')) {
      return ''; // Filter out browser internal errors
    }
    return internalError;
  })();

  // Wrapper to set error safely
  const setError = (msg) => {
    setInternalError(msg || '');
  };

  const setSafeError = setError; // Alias for compatibility

  // Clear any stale errors on mount and filter browser noise
  useEffect(() => {
    setError('');
    
    // Suppress browser errors from showing in console/UI
    const originalError = console.error;
    console.error = (...args) => {
      const msg = args.join(' ');
      if (msg.includes('postMessage') || msg.includes('could not be cloned')) {
        return;
      }
      originalError.apply(console, args);
    };
    
    return () => {
      console.error = originalError;
    };
  }, []);

  useEffect(() => {
    const adminToken = localStorage.getItem('vyta_admin_token');
    if (adminToken) {
      setIsLoggedIn(true);
      loadAnalytics(adminToken);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Use XMLHttpRequest to avoid Edge postMessage issues with fetch
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/admin/login`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onload = function() {
      setLoading(false);
      if (xhr.status === 200) {
        try {
          const data = JSON.parse(xhr.responseText);
          localStorage.setItem('vyta_admin_token', data.access_token);
          setIsLoggedIn(true);
          loadAnalytics(data.access_token);
        } catch (parseErr) {
          setSafeError('Login failed - invalid response');
        }
      } else {
        setSafeError('Invalid credentials');
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      // Don't show error for browser internal issues
      // Just silently fail - user can retry
    };
    
    xhr.send(JSON.stringify(credentials));
  };

  // Helper function to make XHR requests (avoids Edge postMessage issues)
  const xhrRequest = (url, options = {}) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);
      
      // Set headers
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          xhr.setRequestHeader(key, options.headers[key]);
        });
      }
      
      // Handle blob responses
      if (options.responseType) {
        xhr.responseType = options.responseType;
      }
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (options.responseType === 'blob') {
            resolve({ ok: true, blob: () => Promise.resolve(xhr.response) });
          } else {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve({ ok: true, json: () => Promise.resolve(data), data });
            } catch (e) {
              resolve({ ok: true, text: () => Promise.resolve(xhr.responseText) });
            }
          }
        } else {
          resolve({ ok: false, status: xhr.status });
        }
      };
      
      xhr.onerror = function() {
        resolve({ ok: false, status: 0 });
      };
      
      xhr.send(options.body || null);
    });
  };

  const loadAnalytics = async (token) => {
    setLoading(true);
    
    // Helper function to make XHR requests (avoids Edge postMessage issues)
    const xhrGet = (url) => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = function() {
          if (xhr.status === 200) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Parse error'));
            }
          } else if (xhr.status === 401 || xhr.status === 403) {
            reject(new Error('Unauthorized'));
          } else {
            reject(new Error('Request failed'));
          }
        };
        xhr.onerror = function() {
          // Silently resolve with empty data on network errors
          resolve(null);
        };
        xhr.send();
      });
    };
    
    try {
      const [analyticsData, usersData] = await Promise.all([
        xhrGet(`${API_URL}/admin/analytics`),
        xhrGet(`${API_URL}/admin/users?limit=10`),
      ]);

      if (analyticsData) setAnalytics(analyticsData);
      if (usersData) setUsers(usersData.users || []);
    } catch (err) {
      if (err.message === 'Unauthorized') {
        handleLogout();
      }
      // Don't show error for other issues
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('vyta_admin_token');
    setIsLoggedIn(false);
    setAnalytics(null);
    setUsers([]);
  };

  const sendTestReport = async () => {
    setSendingReport(true);
    try {
      const token = localStorage.getItem('vyta_admin_token');
      const response = await xhrRequest(`${API_URL}/admin/send-test-report`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        alert('Test report sent to danny@PFLAB.com.au');
      }
    } catch (err) {
      // Silently fail
    } finally {
      setSendingReport(false);
    }
  };

  const refreshData = () => {
    const token = localStorage.getItem('vyta_admin_token');
    if (token) {
      loadAnalytics(token);
      if (activeTab === 'subscribers') {
        loadSubscribers(token);
      }
    }
  };

  const loadSubscribers = async (token) => {
    setLoadingSubscribers(true);
    try {
      const response = await xhrRequest(`${API_URL}/admin/users/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = response.data || await response.json();
        setSubscribers(data.users || []);
      }
    } catch (err) {
      // Silently fail
    } finally {
      setLoadingSubscribers(false);
    }
  };

  const exportToExcel = async (type = 'all') => {
    setExportingExcel(true);
    try {
      const token = localStorage.getItem('vyta_admin_token');
      const endpoint = type === 'subscribers' ? '/admin/subscribers/export' : '/admin/users/all';
      const response = await xhrRequest(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) return;
      const data = response.data || await response.json();
      const exportData = data.subscribers || data.users || [];
      
      // Format data for Excel
      const formattedData = exportData.map(user => ({
        'Name': user.name || '',
        'Email': user.email || '',
        'Phone': user.phone || '',
        'Gender': user.gender || '',
        'Date of Birth': user.date_of_birth || '',
        'Country': user.country || '',
        'Timezone': user.timezone || '',
        'Subscription Status': user.subscription_status || 'none',
        'Subscription Plan': user.subscription_plan || '',
        'Sign Up Date': user.created_at ? new Date(user.created_at).toLocaleDateString() : '',
        'Trial End': user.trial_end ? new Date(user.trial_end).toLocaleDateString() : '',
        'Height (cm)': user.height_cm || '',
        'Weight (kg)': user.weight_kg || '',
        'Body Type': user.body_type || '',
        'Experience Level': user.experience_level || '',
        'Days/Week': user.days_per_week || '',
        'Session Time (min)': user.time_per_session || '',
        'Training Location': user.training_location || '',
        'Diet Preference': user.dietary_preference || '',
        'Allergies': user.allergies || '',
        'Goals': user.goals || '',
        'Streak Days': user.streak_days || 0,
        'Last Activity': user.last_activity ? new Date(user.last_activity).toLocaleDateString() : '',
      }));
      
      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(formattedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, type === 'subscribers' ? 'Subscribers' : 'All Users');
      
      // Auto-size columns
      const colWidths = Object.keys(formattedData[0] || {}).map(key => ({
        wch: Math.max(key.length, 15)
      }));
      ws['!cols'] = colWidths;
      
      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `VYTA_${type === 'subscribers' ? 'Subscribers' : 'AllUsers'}_${date}.xlsx`;
      
      // Download
      XLSX.writeFile(wb, filename);
    } catch (err) {
      // Silently fail
    } finally {
      setExportingExcel(false);
    }
  };

  const loadChallengeParticipants = async (token) => {
    setLoadingChallenge(true);
    try {
      const response = await xhrRequest(`${API_URL}/admin/challenge/participants`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = response.data || await response.json();
        setChallengeParticipants(data.participants || []);
      }
    } catch (err) {
      // Silently fail
    } finally {
      setLoadingChallenge(false);
    }
  };

  const loadExitSurveys = async (token) => {
    setLoadingExitSurveys(true);
    try {
      const response = await xhrRequest(`${API_URL}/admin/exit-surveys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = response.data || await response.json();
        setExitSurveys(data);
      }
    } catch (err) {
      // Silently fail
    } finally {
      setLoadingExitSurveys(false);
    }
  };

  const exportChallengeToExcel = async () => {
    setExportingExcel(true);
    try {
      const token = localStorage.getItem('vyta_admin_token');
      const response = await xhrRequest(`${API_URL}/admin/challenge/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) return;
      const data = response.data || await response.json();
      const exportData = data.export_data || [];
      
      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Challenge Participants');
      
      // Auto-size columns
      const colWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.max(key.length + 2, 12)
      }));
      ws['!cols'] = colWidths;
      
      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      const filename = `VYTA_Challenge_Participants_${date}.xlsx`;
      
      // Download
      XLSX.writeFile(wb, filename);
    } catch (err) {
      // Silently fail
    } finally {
      setExportingExcel(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn && activeTab === 'subscribers') {
      const token = localStorage.getItem('vyta_admin_token');
      if (token) loadSubscribers(token);
    }
    if (isLoggedIn && activeTab === 'challenge') {
      const token = localStorage.getItem('vyta_admin_token');
      if (token) loadChallengeParticipants(token);
    }
    if (isLoggedIn && activeTab === 'exit-surveys') {
      const token = localStorage.getItem('vyta_admin_token');
      if (token) loadExitSurveys(token);
    }
  }, [activeTab, isLoggedIn]);

  // Login Screen
  if (!isLoggedIn) {
    const doLogin = (e) => {
      e.preventDefault();
      const form = e.target;
      const username = form.username.value;
      const password = form.password.value;
      
      if (!username || !password) {
        setInternalError('Please enter username and password');
        return;
      }
      
      setLoading(true);
      setInternalError('');
      
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/admin/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          setLoading(false);
          if (xhr.status === 200) {
            try {
              const data = JSON.parse(xhr.responseText);
              if (data.access_token) {
                localStorage.setItem('vyta_admin_token', data.access_token);
                setIsLoggedIn(true);
                loadAnalytics(data.access_token);
              } else {
                setInternalError('Invalid credentials');
              }
            } catch (e) {
              setInternalError('Login failed - invalid response');
            }
          } else if (xhr.status === 401 || xhr.status === 403) {
            setInternalError('Invalid credentials');
          } else if (xhr.status === 0) {
            // Network error or CORS - show helpful message
            setInternalError('Connection error - please try again');
          } else {
            setInternalError('Login failed - please try again');
          }
        }
      };
      
      xhr.onerror = function() {
        setLoading(false);
        setInternalError('Connection error - please try again');
      };
      
      try {
        xhr.send(JSON.stringify({ username, password }));
      } catch (e) {
        setLoading(false);
        setInternalError('Connection error');
      }
    };

    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <div className="noise-overlay" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-[#CCFF00] flex items-center justify-center">
              <Shield className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="text-2xl font-black">VYTA Admin</h1>
              <p className="text-xs text-zinc-500">Analytics Dashboard</p>
            </div>
          </div>

          <div className="card-default">
            <form onSubmit={doLogin} className="space-y-4">
              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}
              
              <div>
                <Label className="text-zinc-400">Username</Label>
                <Input
                  type="text"
                  name="username"
                  defaultValue=""
                  className="mt-2 bg-zinc-900 border-zinc-800"
                  placeholder="Admin username"
                  data-testid="admin-username"
                  autoComplete="username"
                />
              </div>

              <div>
                <Label className="text-zinc-400">Password</Label>
                <Input
                  type="password"
                  name="password"
                  defaultValue=""
                  className="mt-2 bg-zinc-900 border-zinc-800"
                  placeholder="••••••••"
                  data-testid="admin-password"
                  autoComplete="current-password"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full"
                data-testid="admin-login-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Access Dashboard'}
              </Button>
            </form>
          </div>

          <p className="text-center text-zinc-600 text-xs mt-6">
            Restricted access. Authorized personnel only.
          </p>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-black pb-8">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer" 
            onClick={() => navigate('/')}
            data-testid="logo-home"
          >
            <div className="w-10 h-10 rounded-xl bg-[#CCFF00] flex items-center justify-center">
              <Shield className="w-5 h-5 text-black" />
            </div>
            <div>
              <h1 className="text-lg font-bold">VYTA Admin</h1>
              <p className="text-xs text-zinc-500">Analytics Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={refreshData}
              disabled={loading}
              className="text-zinc-400 hover:text-white"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-zinc-400 hover:text-white"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        {isLoggedIn && analytics && (
          <div className="flex gap-2 mb-6 border-b border-zinc-800 pb-4 flex-wrap">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'overview' 
                  ? 'bg-[#CCFF00] text-black' 
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('subscribers')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'subscribers' 
                  ? 'bg-[#CCFF00] text-black' 
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
            >
              <Users className="w-4 h-4" />
              Subscribers
            </button>
            <button
              onClick={() => setActiveTab('challenge')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'challenge' 
                  ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-black' 
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
            >
              <Trophy className="w-4 h-4" />
              5 Week Challenge
            </button>
            <button
              onClick={() => setActiveTab('exit-surveys')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'exit-surveys' 
                  ? 'bg-[#CCFF00] text-black' 
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              Exit Surveys
              {exitSurveys.total > 0 && (
                <span className="px-1.5 py-0.5 text-xs rounded-full bg-red-500 text-white">
                  {exitSurveys.total}
                </span>
              )}
            </button>
          </div>
        )}

        {loading && !analytics ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
          </div>
        ) : analytics ? (
          <>
            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
              <>
            {/* Key Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-default bg-gradient-to-br from-[#CCFF00]/10 to-transparent"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#CCFF00]/20 flex items-center justify-center">
                    <Eye className="w-5 h-5 text-[#CCFF00]" />
                  </div>
                  <span className="text-xs text-zinc-500 uppercase">Today's Visits</span>
                </div>
                <p className="text-3xl font-black">{analytics.unique_visits_today}</p>
                <p className={`text-xs mt-1 ${analytics.visit_change_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {analytics.visit_change_percent >= 0 ? '+' : ''}{analytics.visit_change_percent}% vs yesterday
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="card-default"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#14B8A6]/20 flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-[#14B8A6]" />
                  </div>
                  <span className="text-xs text-zinc-500 uppercase">Trial Signups</span>
                </div>
                <p className="text-3xl font-black">{analytics.trial_signups_today}</p>
                <p className="text-xs text-zinc-500 mt-1">Today</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card-default"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#F59E0B]" />
                  </div>
                  <span className="text-xs text-zinc-500 uppercase">Active Trials</span>
                </div>
                <p className="text-3xl font-black">{analytics.total_active_trials}</p>
                <p className="text-xs text-zinc-500 mt-1">Running now</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="card-default"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#6366F1]/20 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-[#6366F1]" />
                  </div>
                  <span className="text-xs text-zinc-500 uppercase">Avg. Session</span>
                </div>
                <p className="text-3xl font-black">{analytics.avg_session_duration_formatted}</p>
                <p className="text-xs text-zinc-500 mt-1">Time on site</p>
              </motion.div>
            </div>

            {/* Subscribers & Errors Row */}
            <div className="grid lg:grid-cols-2 gap-6 mb-8">
              {/* Subscribers */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="card-default"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#CCFF00]" />
                    Active Subscribers
                  </h3>
                  <span className="text-2xl font-black text-[#CCFF00]">{analytics.subscribers.total}</span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 rounded-xl bg-zinc-900">
                    <p className="text-2xl font-black text-[#6366F1]">{analytics.subscribers.weekly}</p>
                    <p className="text-xs text-zinc-500 mt-1">Weekly</p>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-zinc-900 border border-[#CCFF00]/30">
                    <p className="text-2xl font-black text-[#CCFF00]">{analytics.subscribers.monthly}</p>
                    <p className="text-xs text-zinc-500 mt-1">Monthly</p>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-zinc-900">
                    <p className="text-2xl font-black text-[#14B8A6]">{analytics.subscribers.yearly}</p>
                    <p className="text-xs text-zinc-500 mt-1">Yearly</p>
                  </div>
                </div>
              </motion.div>

              {/* Errors */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="card-default"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold flex items-center gap-2">
                    <AlertTriangle className={`w-5 h-5 ${analytics.error_count_24h > 0 ? 'text-red-400' : 'text-green-400'}`} />
                    Website Errors (24h)
                  </h3>
                  <div className="flex items-center gap-2">
                    {analytics.error_count_24h > 0 && (
                      <button
                        onClick={() => {
                          const token = localStorage.getItem('vyta_admin_token');
                          const xhr = new XMLHttpRequest();
                          xhr.open('DELETE', `${API_URL}/admin/clear-errors`, true);
                          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                          xhr.onload = function() {
                            if (xhr.status === 200) {
                              loadAnalytics(token);
                            }
                          };
                          xhr.send();
                        }}
                        className="px-2 py-1 rounded text-xs bg-zinc-700 hover:bg-zinc-600 text-zinc-300 transition-colors"
                      >
                        Clear All
                      </button>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      analytics.error_count_24h > 0 
                        ? 'bg-red-500/20 text-red-400' 
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {analytics.error_count_24h} errors
                    </span>
                  </div>
                </div>
                {analytics.error_count_24h === 0 ? (
                  <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 text-center">
                    ✓ No errors reported in the last 24 hours
                  </div>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {analytics.recent_errors
                      .filter(error => {
                        const msg = error.message || '';
                        // Filter out non-application errors
                        return !msg.includes('Stripe') && 
                               !msg.includes('stripe') &&
                               !msg.includes('Script error') &&
                               !msg.includes('__firefox__') &&
                               !msg.includes('__reader') &&
                               !msg.includes('extension://') &&
                               !msg.includes('WebSocket') &&
                               !msg.includes('postMessage') &&
                               !msg.includes('could not be cloned') &&
                               !msg.includes('Request object') &&
                               !msg.includes('ResizeObserver') &&
                               msg !== 'Script error.' &&
                               msg !== 'Script error';
                      })
                      .slice(0, 5).map((error, i) => (
                      <div key={i} className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                        <p className="text-sm text-red-400 font-medium">{error.error_type}</p>
                        <p className="text-xs text-zinc-500 truncate">{error.message}</p>
                      </div>
                    ))}
                    {analytics.recent_errors.filter(error => {
                      const msg = error.message || '';
                      return !msg.includes('Stripe') && 
                             !msg.includes('stripe') &&
                             !msg.includes('Script error') &&
                             !msg.includes('__firefox__') &&
                             !msg.includes('__reader') &&
                             !msg.includes('extension://') &&
                             !msg.includes('WebSocket') &&
                             !msg.includes('postMessage') &&
                             !msg.includes('could not be cloned') &&
                             !msg.includes('Request object') &&
                             !msg.includes('ResizeObserver') &&
                             msg !== 'Script error.' &&
                             msg !== 'Script error';
                    }).length === 0 && (
                      <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 text-center text-sm">
                        ✓ No application errors - only browser/extension noise detected
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            </div>

            {/* Daily Signups Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="card-default mb-8"
            >
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#CCFF00]" />
                Signups This Week
              </h3>
              <div className="flex items-end justify-between h-32 gap-2">
                {analytics.daily_signups.map((day, i) => {
                  const maxSignups = Math.max(...analytics.daily_signups.map(d => d.signups), 1);
                  const height = (day.signups / maxSignups) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-2">
                      <span className="text-xs font-bold text-[#CCFF00]">{day.signups}</span>
                      <div 
                        className="w-full bg-[#CCFF00]/20 rounded-t-lg transition-all hover:bg-[#CCFF00]/30"
                        style={{ height: `${Math.max(height, 5)}%` }}
                      />
                      <span className="text-xs text-zinc-500">{day.day}</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            {/* Age Groups & Countries */}
            <div className="grid lg:grid-cols-2 gap-6 mb-8">
              {/* Subscribers by Age Group */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.65 }}
                className="card-default"
              >
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-[#CCFF00]" />
                  Subscribers by Age Group
                </h3>
                <div className="space-y-3">
                  {analytics.subscribers_by_age && Object.entries(analytics.subscribers_by_age)
                    .filter(([_, count]) => count > 0)
                    .map(([ageGroup, count], i) => {
                      const total = Object.values(analytics.subscribers_by_age).reduce((a, b) => a + b, 0);
                      const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                      const colors = ['#CCFF00', '#14B8A6', '#6366F1', '#F59E0B', '#EF4444', '#8B5CF6'];
                      return (
                        <div key={ageGroup} className="flex items-center gap-3">
                          <div className="w-16 text-sm font-medium text-zinc-300">{ageGroup}</div>
                          <div className="flex-1 h-6 bg-zinc-900 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all"
                              style={{ 
                                width: `${percentage}%`, 
                                backgroundColor: colors[i % colors.length],
                                minWidth: count > 0 ? '8px' : '0'
                              }}
                            />
                          </div>
                          <div className="w-12 text-right">
                            <span className="text-sm font-bold">{count}</span>
                            <span className="text-xs text-zinc-500 ml-1">({percentage}%)</span>
                          </div>
                        </div>
                      );
                    })}
                  {(!analytics.subscribers_by_age || Object.values(analytics.subscribers_by_age).every(v => v === 0)) && (
                    <div className="text-center py-6 text-zinc-500 text-sm">
                      No subscriber data available yet
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Subscribers by Country */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="card-default"
              >
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#CCFF00]" />
                  Subscribers by Country
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {analytics.subscribers_by_country && analytics.subscribers_by_country.length > 0 ? (
                    analytics.subscribers_by_country.map((item, i) => {
                      const total = analytics.subscribers_by_country.reduce((a, b) => a + b.count, 0);
                      const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
                      return (
                        <div key={item.country} className="flex items-center justify-between p-3 rounded-lg bg-zinc-900 hover:bg-zinc-800 transition-colors">
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{getCountryFlag(item.country)}</span>
                            <span className="font-medium text-sm">{item.country}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-2 bg-zinc-800 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-[#CCFF00] rounded-full"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-sm font-bold w-8 text-right">{item.count}</span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-6 text-zinc-500 text-sm">
                      No country data available yet
                    </div>
                  )}
                </div>
              </motion.div>
            </div>

            {/* Recent Users & Actions */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Recent Users */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="card-default"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#CCFF00]" />
                    Recent Users
                  </h3>
                  <span className="text-sm text-zinc-500">{analytics.total_users} total</span>
                </div>
                <div className="space-y-3">
                  {users.slice(0, 5).map((user, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-zinc-900">
                      <div>
                        <p className="font-medium text-sm">{user.name || 'No name'}</p>
                        <p className="text-xs text-zinc-500">{user.email}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        user.subscription_status === 'active' 
                          ? 'bg-green-500/20 text-green-400'
                          : user.subscription_status === 'trialing'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-zinc-800 text-zinc-400'
                      }`}>
                        {user.subscription_status || 'none'}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Quick Actions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="card-default"
              >
                <h3 className="font-bold mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <Button
                    onClick={sendTestReport}
                    disabled={sendingReport}
                    className="w-full justify-between bg-zinc-900 hover:bg-zinc-800 text-left"
                  >
                    <span className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#CCFF00]" />
                      Send Test Report Email
                    </span>
                    {sendingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
                  </Button>
                  <div className="p-4 rounded-xl bg-zinc-900">
                    <p className="text-sm text-zinc-400 mb-2">Daily Report Schedule</p>
                    <p className="font-bold">8:00 AM AEST</p>
                    <p className="text-xs text-zinc-500">Sent to danny@PFLAB.com.au</p>
                  </div>
                  <div className="p-4 rounded-xl bg-zinc-900">
                    <p className="text-sm text-zinc-400 mb-2">Last Updated</p>
                    <p className="font-mono text-sm">
                      {new Date(analytics.generated_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
            </>
            )}

            {/* SUBSCRIBERS TAB */}
            {activeTab === 'subscribers' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {/* Export Buttons */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <Button
                    onClick={() => exportToExcel('subscribers')}
                    disabled={exportingExcel}
                    className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold"
                  >
                    {exportingExcel ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                    )}
                    Export Active Subscribers
                  </Button>
                  <Button
                    onClick={() => exportToExcel('all')}
                    disabled={exportingExcel}
                    variant="outline"
                    className="border-zinc-700"
                  >
                    {exportingExcel ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Export All Users
                  </Button>
                </div>

                {/* Subscribers Table */}
                <div className="card-default overflow-hidden">
                  <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                    <h3 className="font-bold flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#CCFF00]" />
                      All Users ({subscribers.length})
                    </h3>
                  </div>
                  
                  {loadingSubscribers ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-zinc-900">
                          <tr>
                            <th className="text-left p-3 font-medium text-zinc-400">Name</th>
                            <th className="text-left p-3 font-medium text-zinc-400">Email</th>
                            <th className="text-left p-3 font-medium text-zinc-400">Phone</th>
                            <th className="text-left p-3 font-medium text-zinc-400">Country</th>
                            <th className="text-left p-3 font-medium text-zinc-400">Gender</th>
                            <th className="text-left p-3 font-medium text-zinc-400">Status</th>
                            <th className="text-left p-3 font-medium text-zinc-400">Plan</th>
                            <th className="text-left p-3 font-medium text-zinc-400">Signed Up</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subscribers.map((user, i) => (
                            <tr key={user.id || i} className="border-t border-zinc-800 hover:bg-zinc-900/50">
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
                                    <User className="w-4 h-4 text-zinc-500" />
                                  </div>
                                  <span className="font-medium">{user.name || '—'}</span>
                                </div>
                              </td>
                              <td className="p-3 text-zinc-400">{user.email}</td>
                              <td className="p-3 text-zinc-400">
                                {user.phone ? (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {user.phone}
                                  </span>
                                ) : '—'}
                              </td>
                              <td className="p-3">
                                {user.country ? (
                                  <span className="flex items-center gap-1">
                                    <span>{getCountryFlag(user.country)}</span>
                                    {user.country}
                                  </span>
                                ) : '—'}
                              </td>
                              <td className="p-3 text-zinc-400 capitalize">{user.gender || '—'}</td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                  user.subscription_status === 'active' 
                                    ? 'bg-green-500/20 text-green-400'
                                    : user.subscription_status === 'trialing'
                                    ? 'bg-yellow-500/20 text-yellow-400'
                                    : user.subscription_status === 'registered'
                                    ? 'bg-blue-500/20 text-blue-400'
                                    : user.subscription_status === 'pending'
                                    ? 'bg-orange-500/20 text-orange-400'
                                    : 'bg-zinc-800 text-zinc-500'
                                }`}>
                                  {user.subscription_status === 'registered' ? 'Registered' 
                                    : user.subscription_status === 'pending' ? 'Pending'
                                    : user.subscription_status || 'none'}
                                </span>
                              </td>
                              <td className="p-3 text-zinc-400 capitalize">{user.subscription_plan || '—'}</td>
                              <td className="p-3 text-zinc-500 text-xs">
                                {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                              </td>
                            </tr>
                          ))}
                          {subscribers.length === 0 && (
                            <tr>
                              <td colSpan={8} className="p-8 text-center text-zinc-500">
                                No users found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Export Info */}
                <div className="mt-4 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
                  <p className="text-sm text-zinc-400">
                    <strong className="text-white">Excel Export includes:</strong> Name, Email, Phone, Gender, DOB, Country, Timezone, Subscription Status/Plan, Height, Weight, Body Type, Experience Level, Training Days/Location, Diet Preferences, Allergies, Goals, Activity Data
                  </p>
                </div>
              </motion.div>
            )}

            {/* CHALLENGE TAB */}
            {activeTab === 'challenge' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Challenge Header */}
                <div className="p-6 rounded-2xl bg-gradient-to-r from-yellow-400/10 via-amber-500/10 to-yellow-400/10 border border-yellow-400/30">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <Trophy className="w-8 h-8 text-yellow-400" />
                        <h2 className="text-2xl font-black">5 Week Global Challenge</h2>
                      </div>
                      <p className="text-zinc-400">Jan 23 - Feb 27, 2025 • USD $2,000 Prize Pool</p>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={() => loadChallengeParticipants(localStorage.getItem('vyta_admin_token'))}
                        variant="outline"
                        className="border-yellow-400/50 text-yellow-400 hover:bg-yellow-400/10"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                      </Button>
                      <Button
                        onClick={exportChallengeToExcel}
                        disabled={exportingExcel}
                        className="bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold hover:from-yellow-300 hover:to-amber-400"
                      >
                        {exportingExcel ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                        )}
                        Export to Excel
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-5 h-5 text-yellow-400" />
                      <span className="text-sm text-zinc-400">Participants</span>
                    </div>
                    <p className="text-3xl font-black">{challengeParticipants.length}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-5 h-5 text-[#CCFF00]" />
                      <span className="text-sm text-zinc-400">Top Score</span>
                    </div>
                    <p className="text-3xl font-black text-[#CCFF00]">
                      {challengeParticipants[0]?.score?.final_score || 0}
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-5 h-5 text-blue-400" />
                      <span className="text-sm text-zinc-400">Avg Adherence</span>
                    </div>
                    <p className="text-3xl font-black">
                      {challengeParticipants.length > 0 
                        ? Math.round(challengeParticipants.reduce((a, p) => a + (p.score?.plan_adherence || 0), 0) / challengeParticipants.length)
                        : 0}%
                    </p>
                  </div>
                  <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Scale className="w-5 h-5 text-emerald-400" />
                      <span className="text-sm text-zinc-400">Avg Transformation</span>
                    </div>
                    <p className="text-3xl font-black">
                      {challengeParticipants.length > 0 
                        ? Math.round(challengeParticipants.reduce((a, p) => a + (p.score?.physical_transformation || 0), 0) / challengeParticipants.length)
                        : 0}%
                    </p>
                  </div>
                </div>

                {/* Podium - Top 3 */}
                {challengeParticipants.length >= 1 && (
                  <div className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800">
                    <h3 className="font-bold text-lg mb-6 flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-yellow-400" />
                      Current Standings
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      {challengeParticipants.slice(0, 3).map((p, i) => (
                        <div 
                          key={p.user_id}
                          className={`p-5 rounded-xl border text-center ${
                            i === 0 ? 'bg-yellow-400/10 border-yellow-400/30' :
                            i === 1 ? 'bg-zinc-400/10 border-zinc-400/30' :
                            'bg-amber-600/10 border-amber-600/30'
                          }`}
                        >
                          <div className="text-4xl mb-2">{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</div>
                          <div className="text-lg font-bold mb-1">{p.name}</div>
                          <div className="text-sm text-zinc-400 mb-3">{p.email}</div>
                          <div className={`text-3xl font-black ${
                            i === 0 ? 'text-yellow-400' : i === 1 ? 'text-zinc-300' : 'text-amber-500'
                          }`}>
                            {p.score?.final_score || 0}
                          </div>
                          <div className="text-xs text-zinc-500 mt-1">Final Score</div>
                          <div className={`mt-3 text-lg font-bold ${
                            i === 0 ? 'text-yellow-400' : i === 1 ? 'text-zinc-300' : 'text-amber-500'
                          }`}>
                            {p.prize}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Participants Table */}
                <div className="card-default overflow-hidden">
                  <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                    <h3 className="font-bold flex items-center gap-2">
                      <Users className="w-5 h-5 text-yellow-400" />
                      All Challenge Participants ({challengeParticipants.length})
                    </h3>
                  </div>
                  
                  {loadingChallenge ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 text-yellow-400 animate-spin" />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-zinc-900">
                          <tr>
                            <th className="text-left p-3 font-medium text-zinc-400">Rank</th>
                            <th className="text-left p-3 font-medium text-zinc-400">Name</th>
                            <th className="text-left p-3 font-medium text-zinc-400">Email</th>
                            <th className="text-left p-3 font-medium text-zinc-400">Country</th>
                            <th className="text-center p-3 font-medium text-zinc-400">Weight Δ</th>
                            <th className="text-center p-3 font-medium text-zinc-400">Waist Δ</th>
                            <th className="text-center p-3 font-medium text-zinc-400">Sessions</th>
                            <th className="text-center p-3 font-medium text-zinc-400">Adherence</th>
                            <th className="text-center p-3 font-medium text-zinc-400">Transform</th>
                            <th className="text-center p-3 font-medium text-[#CCFF00]">Final Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {challengeParticipants.map((p, i) => {
                            const weightChange = p.score?.transformations?.find(t => t.metric === 'weight')?.change;
                            const waistChange = p.score?.transformations?.find(t => t.metric === 'waist')?.change;
                            return (
                              <tr key={p.user_id} className={`border-t border-zinc-800 hover:bg-zinc-900/50 ${i < 3 ? 'bg-yellow-400/5' : ''}`}>
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    {i === 0 && <span className="text-lg">🥇</span>}
                                    {i === 1 && <span className="text-lg">🥈</span>}
                                    {i === 2 && <span className="text-lg">🥉</span>}
                                    <span className={`font-bold ${i < 3 ? 'text-yellow-400' : ''}`}>#{p.rank}</span>
                                  </div>
                                </td>
                                <td className="p-3 font-medium">{p.name || '—'}</td>
                                <td className="p-3 text-zinc-400">{p.email}</td>
                                <td className="p-3">
                                  {p.country ? (
                                    <span className="flex items-center gap-1">
                                      <span>{getCountryFlag(p.country)}</span>
                                      {p.country}
                                    </span>
                                  ) : '—'}
                                </td>
                                <td className="p-3 text-center">
                                  {weightChange !== undefined && weightChange !== null ? (
                                    <span className={weightChange < 0 ? 'text-emerald-400' : weightChange > 0 ? 'text-red-400' : ''}>
                                      {weightChange > 0 ? '+' : ''}{weightChange} kg
                                    </span>
                                  ) : '—'}
                                </td>
                                <td className="p-3 text-center">
                                  {waistChange !== undefined && waistChange !== null ? (
                                    <span className={waistChange < 0 ? 'text-emerald-400' : waistChange > 0 ? 'text-red-400' : ''}>
                                      {waistChange > 0 ? '+' : ''}{waistChange} cm
                                    </span>
                                  ) : '—'}
                                </td>
                                <td className="p-3 text-center">
                                  <span className="text-zinc-300">{p.score?.sessions_logged || 0}</span>
                                  <span className="text-zinc-500">/{p.score?.sessions_expected || 0}</span>
                                </td>
                                <td className="p-3 text-center">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    (p.score?.plan_adherence || 0) >= 80 ? 'bg-emerald-500/20 text-emerald-400' :
                                    (p.score?.plan_adherence || 0) >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-red-500/20 text-red-400'
                                  }`}>
                                    {p.score?.plan_adherence || 0}%
                                  </span>
                                </td>
                                <td className="p-3 text-center">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    (p.score?.physical_transformation || 0) >= 50 ? 'bg-emerald-500/20 text-emerald-400' :
                                    (p.score?.physical_transformation || 0) >= 25 ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-zinc-700 text-zinc-400'
                                  }`}>
                                    {p.score?.physical_transformation || 0}%
                                  </span>
                                </td>
                                <td className="p-3 text-center">
                                  <span className={`text-lg font-black ${i < 3 ? 'text-yellow-400' : 'text-[#CCFF00]'}`}>
                                    {p.score?.final_score || 0}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                          {challengeParticipants.length === 0 && (
                            <tr>
                              <td colSpan={10} className="p-8 text-center text-zinc-500">
                                No challenge participants yet
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Scoring Info */}
                <div className="p-6 rounded-xl bg-zinc-900/50 border border-zinc-800">
                  <h4 className="font-bold mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-yellow-400" />
                    Scoring Criteria Weights
                  </h4>
                  <div className="grid sm:grid-cols-5 gap-4">
                    {[
                      { name: 'Plan Adherence', weight: '25%', color: 'text-blue-400' },
                      { name: 'Physical Transformation', weight: '25%', color: 'text-emerald-400' },
                      { name: 'Performance', weight: '20%', color: 'text-purple-400' },
                      { name: 'Engagement', weight: '15%', color: 'text-orange-400' },
                      { name: 'Overall Impact', weight: '15%', color: 'text-pink-400' },
                    ].map((c) => (
                      <div key={c.name} className="text-center p-3 rounded-lg bg-zinc-800/50">
                        <div className={`text-xl font-bold ${c.color}`}>{c.weight}</div>
                        <div className="text-xs text-zinc-400 mt-1">{c.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* EXIT SURVEYS TAB */}
            {activeTab === 'exit-surveys' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Header */}
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-black flex items-center gap-3">
                      <MessageSquare className="w-7 h-7 text-[#CCFF00]" />
                      Exit Survey Responses
                    </h2>
                    <p className="text-zinc-400">Why users leave without subscribing</p>
                  </div>
                  <Button
                    onClick={() => loadExitSurveys(localStorage.getItem('vyta_admin_token'))}
                    variant="outline"
                    className="border-zinc-700"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>

                {loadingExitSurveys ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* Reason Stats */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                      {exitSurveys.reason_stats.length > 0 ? (
                        exitSurveys.reason_stats.map((stat, i) => {
                          const colors = ['text-red-400', 'text-orange-400', 'text-yellow-400', 'text-blue-400', 'text-purple-400'];
                          const bgColors = ['bg-red-500/10', 'bg-orange-500/10', 'bg-yellow-500/10', 'bg-blue-500/10', 'bg-purple-500/10'];
                          return (
                            <div key={stat.reason_id} className={`p-4 rounded-xl ${bgColors[i % 5]} border border-zinc-800`}>
                              <div className={`text-3xl font-black ${colors[i % 5]}`}>{stat.count}</div>
                              <div className="text-sm text-zinc-400 mt-1">{stat.label}</div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="col-span-full p-8 text-center text-zinc-500 bg-zinc-900/50 rounded-xl">
                          No exit survey responses yet
                        </div>
                      )}
                    </div>

                    {/* Recent Responses */}
                    {exitSurveys.surveys.length > 0 && (
                      <div className="card-default overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                          <h3 className="font-bold flex items-center gap-2">
                            <MessageSquare className="w-5 h-5 text-[#CCFF00]" />
                            Recent Responses ({exitSurveys.total})
                          </h3>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-zinc-900">
                              <tr>
                                <th className="text-left p-3 font-medium text-zinc-400">User</th>
                                <th className="text-left p-3 font-medium text-zinc-400">Email</th>
                                <th className="text-left p-3 font-medium text-zinc-400">Reason</th>
                                <th className="text-left p-3 font-medium text-zinc-400">Details</th>
                                <th className="text-left p-3 font-medium text-zinc-400">Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {exitSurveys.surveys.slice(0, 50).map((survey, i) => {
                                const reasonLabel = EXIT_REASONS.find(r => r.id === survey.reason)?.label || survey.reason;
                                return (
                                  <tr key={survey.id || i} className="border-t border-zinc-800 hover:bg-zinc-900/50">
                                    <td className="p-3 font-medium">{survey.user_name || '—'}</td>
                                    <td className="p-3 text-zinc-400">{survey.user_email}</td>
                                    <td className="p-3">
                                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                        survey.reason === 'too_expensive' ? 'bg-red-500/20 text-red-400' :
                                        survey.reason === 'not_ready' ? 'bg-orange-500/20 text-orange-400' :
                                        survey.reason === 'found_alternative' ? 'bg-yellow-500/20 text-yellow-400' :
                                        survey.reason === 'just_browsing' ? 'bg-blue-500/20 text-blue-400' :
                                        'bg-purple-500/20 text-purple-400'
                                      }`}>
                                        {reasonLabel}
                                      </span>
                                    </td>
                                    <td className="p-3 text-zinc-400 max-w-[200px] truncate">
                                      {survey.other_details || '—'}
                                    </td>
                                    <td className="p-3 text-zinc-500 text-xs">
                                      {survey.submitted_at ? new Date(survey.submitted_at).toLocaleDateString() : '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </>
        ) : null}
      </main>
    </div>
  );
}
